import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore'
import type { InventoryLeadLostReason } from '@otw/models/leads/inventoryLead'
import { db } from '@/config/firebase'
import { useAuth } from '@/contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'

const COLLECTION = 'inventory_lead_lost_reasons'
export const inventoryLeadLostReasonsQueryKey = ['inventory_lead_lost_reasons'] as const

const mapReasonDoc = (
  snapshot: QueryDocumentSnapshot<DocumentData>,
): InventoryLeadLostReason | null => {
  const data = snapshot.data()
  const label = typeof data.label === 'string' ? data.label.trim() : ''
  if (!label) return null
  return {
    id: snapshot.id,
    label,
    createdAt: typeof data.createdAt === 'string' ? data.createdAt : '',
    createdBy: typeof data.createdBy === 'string' ? data.createdBy : '',
  }
}

/** CRUD for shared inventory-lead lost reasons (Firestore `inventory_lead_lost_reasons`). */
export const useInventoryLeadLostReasons = (enabled = true) => {
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()
  const reasonsCollection = collection(db, COLLECTION)

  const {
    data: reasons = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: inventoryLeadLostReasonsQueryKey,
    queryFn: async (): Promise<InventoryLeadLostReason[]> => {
      const snapshot = await getDocs(reasonsCollection)
      return snapshot.docs
        .map(mapReasonDoc)
        .filter((reason): reason is InventoryLeadLostReason => reason !== null)
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
    },
    staleTime: 1000 * 60 * 5,
    enabled,
  })

  const createMutation = useMutation({
    mutationFn: async (label: string): Promise<InventoryLeadLostReason> => {
      const trimmed = label.trim()
      if (!trimmed) throw new Error('Reason label is required')

      const existing = reasons.find(
        (reason) => reason.label.localeCompare(trimmed, undefined, { sensitivity: 'base' }) === 0,
      )
      if (existing) return existing

      const now = new Date().toISOString()
      const payload = {
        label: trimmed,
        createdAt: now,
        createdBy: userInfo?.id ? String(userInfo.id) : '',
      }
      const ref = await addDoc(reasonsCollection, payload)
      return { id: ref.id, ...payload }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: inventoryLeadLostReasonsQueryKey })
      showSuccess('Lost reason saved')
    },
    onError: () => showError('Could not save lost reason'),
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, COLLECTION, id))
      return id
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: inventoryLeadLostReasonsQueryKey })
      showSuccess('Lost reason deleted')
    },
    onError: () => showError('Could not delete lost reason'),
  })

  return {
    reasons,
    isLoading,
    refetch,
    createLostReason: createMutation.mutateAsync,
    deleteLostReason: deleteMutation.mutateAsync,
    isCreating: createMutation.isPending,
  }
}
